// src/pages/UsersPage.js
import React, { useState, useEffect } from 'react';
import apiClient from '../services/ApiClient'; // API istemcisini import et
import LoadingSpinner from '../components/common/LoadingSpinner'; // Yükleme göstergesi
import styles from './UsersPage.module.css'; // Oluşturduğumuz CSS modülünü import et
import { toast } from 'react-toastify';
import Modal from '../components/common/Modal';

// ID'ler çoğunlukla sayı gelir ama string dönen kurulumlarda da doğru sıralansın.
const compareById = (a, b) => {
  const aNum = Number(a?.id);
  const bNum = Number(b?.id);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
  return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
};

const UsersPage = () => {

  const [users, setUsers] = useState([]); // Kullanıcı listesini tutacak state
  const [loading, setLoading] = useState(true); // Yükleme durumunu tutacak state (başlangıçta true)
  const [error, setError] = useState(null); // Hata mesajını tutacak state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editIsAdmin, setEditIsAdmin] = useState(false);

  // Veri Çekme Fonksiyonu
  const fetchUsers = async () => {
    setLoading(true); // İstek başlarken yükleme durumunu true yap
    setError(null); // Önceki hataları temizle
    try {
      // !!! ÖNEMLİ: '/users' kısmını kendi gerçek API endpoint'iniz ile değiştirin !!!
      const response = await apiClient.get('/allUsers');

      // API'den dönen verinin yapısına göre ayarlama yapın.
      // Genellikle veriler response.data içinde bir dizi olarak gelir.
      if (Array.isArray(response.data)) {
        setUsers([...response.data].sort(compareById));
      } else {
        // Eğer API farklı bir formatta (örn: { data: [...] } gibi) dönüyorsa:
        // setUsers(response.data.data || []); // veya uygun alan neyse onu kullanın
        console.warn("API'den beklenen formatta (dizi) kullanıcı verisi gelmedi:", response.data);
        setUsers([]); // Hata olmasa bile boş dizi ata
      }

    } catch (err) {
      console.error("Kullanıcıları çekerken hata oluştu:", err);
      setError("Kullanıcı verileri yüklenemedi. Lütfen daha sonra tekrar deneyin."); // Kullanıcı dostu hata mesajı
      setUsers([]); // Hata durumunda kullanıcı listesini boşalt
    } finally {
      setLoading(false); // İstek tamamlandığında (başarılı veya hatalı) yükleme durumunu false yap
    }
  };

  // Bileşen ilk yüklendiğinde veriyi çekmek için useEffect kullan
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Boş dependency array [], bu effect'in sadece component mount olduğunda çalışmasını sağlar
  const handleEditUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    setSelectedUser(user);
    setEditRole(user.role || 'N/A');
    setEditIsAdmin(!!user.isAdmin);
    setIsEditModalOpen(true);
  };
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
    setEditRole('');
    setEditIsAdmin(false);
  };

  const handleSaveUserRole = async () => {
    if (!selectedUser) return;
    setEditLoading(true);
    try {
      await apiClient.patch(`/users/${selectedUser.id}`, { role: "admin", isAdmin: editIsAdmin });

      setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, role: editRole, isAdmin: editIsAdmin } : u));
      toast.success('Kullanıcı güncellendi!');
      handleCloseEditModal();
    } catch (err) {
      toast.error('Kullanıcı güncellenemedi.');
    } finally {
      setEditLoading(false);
    }
  };
  // handleDeleteUser fonksiyonu ve ilgili kodlar kaldırıldı
  // --- Render Logic ---

  // --- Page header (her durumda görünür) ---
  const pageHeader = (
    <div className={styles.header}>
      <div className={styles.headerText}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Kullanıcılar</h1>
          {!loading && !error && (
            <span className={`${styles.count} num`}>{users.length}</span>
          )}
        </div>
        <p className={styles.subtitle}>Kayıtlı kullanıcıları görüntüleyin ve yetkilerini düzenleyin.</p>
      </div>
      {/* Yeni Kullanıcı Ekle butonu kaldırıldı */}
    </div>
  );

  // Yükleme durumu: sayfa başlığı korunur, spinner çerçeve içinde
  if (loading) {
    return (
      <div className={styles.container}>
        {pageHeader}
        <LoadingSpinner label="Kullanıcılar yükleniyor..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        {pageHeader}
        <div className={styles.errorPanel} role="alert">
          <span className={styles.errorIcon} aria-hidden="true">⚠</span>
          <p className={styles.errorMessage}>
            Kullanıcı verileri alınamadı. Bağlantıyı kontrol edip tekrar deneyin.
          </p>
          <button onClick={fetchUsers} className={styles.retryButton}>Tekrar dene</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {pageHeader}

      {users.length === 0 ? (
        <div className={styles.emptyState}>Gösterilecek kullanıcı bulunamadı.</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.usersTable}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Ad Soyad</th>
                <th>Email</th>
                <th>Rol</th>
                <th className={styles.actionsCol}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="num">{user.id}</td>
                  <td>{user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}</td>
                  <td>{user.email || 'N/A'}</td>
                  <td>
                    <span className={`${styles.roleBadge} ${user.isAdmin ? styles.roleBadgeAdmin : ''}`}>
                      {user.isAdmin ? 'Admin' : (user.role || 'Kullanıcı')}
                    </span>
                  </td>
                  <td className={styles.actionsCol}>
                    <div className={styles.actionButtons}>
                      <button
                        onClick={() => handleEditUser(user.id)}
                        className={`${styles.actionButton} ${styles.editButton}`}
                        title="Düzenle"
                      >
                        Düzenle
                      </button>
                      {/* Sil butonu kaldırıldı */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal isOpen={isEditModalOpen} onClose={handleCloseEditModal} title="Kullanıcıyı Düzenle">
        {selectedUser && (
          <div>
            <p className={styles.modalMeta}><strong>Ad Soyad:</strong> {selectedUser.fullName || `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || 'N/A'}</p>
            <p className={styles.modalMeta}><strong>Email:</strong> {selectedUser.email || 'N/A'}</p>
            <div className={styles.modalField}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={editIsAdmin}
                  onChange={e => setEditIsAdmin(e.target.checked)}
                  disabled={editLoading}
                />
                Admin olarak işaretle
              </label>
            </div>
            <div className={styles.modalActions}>
              <button onClick={handleCloseEditModal} disabled={editLoading} className={styles.cancelButton}>İptal</button>
              <button onClick={handleSaveUserRole} disabled={editLoading}>{editLoading ? 'Kaydediliyor...' : 'Kaydet'}</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};


export default UsersPage;